import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import FileInput from './FileInput';
import styles from './styles';

const UploadDocuments = () => {
  const { token } = useParams();
  const [linkToken, setLinkToken] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [files, setFiles] = useState({});
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewMap, setPreviewMap] = useState({});
  const [modalContent, setModalContent] = useState(null);
  const hardcodedMsgToken = "1EA7B5179FCF5F583A6AA795269C4";

  const fetchPreviewMap = async (token) => {
    try {
      const res = await axios.get(
        'https://be.corebankers.in/wa-msg91/wa-preview',
        {
          headers: { Authorization: `Bearer ${hardcodedMsgToken}` },
          params: { token },
        }
      );

      const docs = res.data?.documents || {};
      const map = {};

      Object.entries(docs).forEach(([key, { mimeType, base64 }]) => {
        if (base64 && mimeType) {
          const blob = new Blob([Uint8Array.from(atob(base64), c => c.charCodeAt(0))], { type: mimeType });
          const url = URL.createObjectURL(blob);
          map[key] = { url, mimeType };
        }
      });

      setPreviewMap(map);
    } catch (err) {
      console.error('❌ Failed to fetch preview map:', err);
      setPreviewMap({});
    }
  };

  useEffect(() => {
    if (!token) {
      setIsTokenValid(false);
      setMessage('❌ Missing link token.');
      return;
    }

    setLinkToken(token);

    const validateToken = async () => {
      try {
        const res = await axios.get(
          'https://be.corebankers.in/wa-msg91/wa-validate-token',
          {
            headers: { Authorization: `Bearer ${hardcodedMsgToken}` },
            params: { token },
          }
        );

        const isValid = res.data?.success === true;
        setIsTokenValid(isValid);

        if (isValid) {
          fetchPreviewMap(token);
        } else {
          setMessage('❌ Invalid or expired link token.');
        }
      } catch (err) {
        setIsTokenValid(false);
        setMessage('❌ Error validating token.');
      }
    };

    validateToken();
  }, [token]);

  const handleFileChange = (field, file) => {
    setFiles(prev => ({ ...prev, [field]: file }));
  };

  const handlePreview = (url, mimeType) => {
    setModalContent({ url, mimeType });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    const formData = new FormData();
    Object.entries(files).forEach(([key, file]) => {
      if (file) formData.append(key, file);
    });

    try {
      const res = await axios.post(
        'https://be.corebankers.in/wa-msg91/wa-upload',
        formData,
        {
          headers: { Authorization: `Bearer ${hardcodedMsgToken}` },
          params: { token: linkToken },
        }
      );

      if (res.data.message?.includes("successfully")) {
        setMessage('✅ Documents uploaded successfully!');
        setFiles({});
        fetchPreviewMap(linkToken);
      } else {
        setMessage('❌ Upload failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
      setMessage('❌ Error uploading documents.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2>Upload Documents</h2>
      <p style={styles.note}>* Only PNG, JPG/JPEG, or PDF formats are accepted</p>

      {isTokenValid === null && <p>🔄 Validating link...</p>}
      {isTokenValid === false && <p>{message}</p>}

      {isTokenValid === true && (
        <>
          <form onSubmit={handleSubmit} style={styles.form}>
            {[
              { label: "Aadhar Front", field: "aadhar_front_url" },
              { label: "Aadhar Back", field: "aadhar_back_url" },
              { label: "PAN Card", field: "pan_url" },
              { label: "Photo", field: "photo_url", accept: "image/*" },
              { label: "6-month Bank Statement", field: "bank_statement_url", accept: "application/pdf" },
              { label: "Payslip - Month 1", field: "back_f_url", accept: "application/pdf" },
              { label: "Payslip - Month 2", field: "back_s_url", accept: "application/pdf" },
              { label: "Payslip - Month 3", field: "back_t_url", accept: "application/pdf" },
            ].map(({ label, field, accept }) => (
              <FileInput
                key={field}
                label={label}
                field={field}
                accept={accept}
                preview={previewMap[field]}
                onFileChange={handleFileChange}
                onPreview={handlePreview}
              />
            ))}

            <button type="submit" style={styles.button} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Documents'}
            </button>
          </form>

          {uploading && <p>🔄 Uploading documents, please wait...</p>}
          {message && <p>{message}</p>}
        </>
      )}

      {modalContent && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button onClick={() => setModalContent(null)} style={modalStyles.close}>✖</button>
            {modalContent.mimeType.startsWith("image/") && (
              <img src={modalContent.url} alt="Preview" style={modalStyles.content} />
            )}
            {modalContent.mimeType === "application/pdf" && (
              <iframe src={modalContent.url} title="PDF Preview" style={modalStyles.content} />
            )}
            {!modalContent.mimeType.startsWith("image/") && modalContent.mimeType !== "application/pdf" && (
              <p>Unsupported format</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center"
  },
  modal: {
    background: "#fff", padding: "1rem", borderRadius: "8px", position: "relative", width: "80%", maxHeight: "80%", overflow: "auto"
  },
  close: {
    position: "absolute", top: "10px", right: "10px", background: "transparent", border: "none", fontSize: "1.2rem"
  },
  content: {
    width: "100%", height: "600px", objectFit: "contain"
  }
};

export default UploadDocuments;